import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import JSZip from 'jszip';
import { findCityPages, buildSitemapXml, buildRegistry } from '../services/guidesSite';
import { buildIndexHtml, findCountryPages, generateCountryPage } from '../services/HubPageGenerator';

// Same rule as GenerateAllSeoButton: only English guides get a page, so only they are listed
const isEnglish = (language) => /^en([-_].*)?$/i.test(String(language).trim()) || /^english$/i.test(String(language).trim());

/**
 * Regenerates index.html, the country pages, sitemap.xml and guides-registry.js without re-downloading all guide pages.
 * Uses the same code as GenerateAllSeoButton (services/guidesSite.js). City pages themselves are only
 * built by "Generate & Download All", because they need each guide's stops and audio.
 */
export const GenerateIndexOnlyButton = () => {
  const { currentUser } = useAuth();
  const [generating, setGenerating] = useState(false);

  const API_BASE_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net';

  const handleGenerate = async () => {
    if (!currentUser) { toast.error('You must be logged in'); return; }

    setGenerating(true);
    const loadingToast = toast.loading('Fetching guides...');

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/getGuides`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!data.success || !data.guides) throw new Error('Failed to fetch guides');

      toast.dismiss(loadingToast);
      toast.loading('Building index & sitemap...');

      const guides = data.guides.filter(g => g.Name && g.Website && (!g.Language || isEnglish(g.Language)));
      // the same city pages as "Generate All": guides plus the planner's research (public/city-research)
      let research = {};
      try {
        const res = await fetch('/city-research/index.json');
        if (res.ok) research = await res.json();
      } catch (e) {
        console.warn('[Generate Index] no city research found:', e);
      }
      const cityPages = findCityPages(guides, research);
      const countryPages = findCountryPages(guides, cityPages);
      const zip = new JSZip();
      // the world's best-known sights for the globe (public/globe/places.json, guide-studio/globe_places.py)
      let sights = [];
      try {
        const res = await fetch('/globe/places.json');
        if (res.ok) sights = (await res.json()).places || [];
      } catch (e) {
        console.warn('No globe sights found, the globe shows our cities and guides only:', e);
      }
      zip.file('index.html', buildIndexHtml(guides, cityPages, sights));
      countryPages.forEach(k => zip.file(`${k.countrySlug}/index.html`, generateCountryPage(k, guides)));
      zip.file('sitemap.xml', buildSitemapXml(guides, cityPages, countryPages));
      zip.file('guides-registry.js', buildRegistry(guides, cityPages));
      // the globe in the index hero is new; ship it with the index so the page never points at a missing file
      const globe = await fetch('/guides-globe.js');
      if (globe.ok) zip.file('guides-globe.js', await globe.text());

      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `guides-index-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success(
        `index.html, ${countryPages.length} country pages, sitemap.xml and guides-registry.js for ${guides.length} English guides and ${cityPages.length} city pages. ` +
        'If a new city reached the threshold, run "Generate & Download All" to build its page.',
        { duration: 7000 }
      );

    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(`Failed: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <button
      onClick={handleGenerate}
      disabled={generating}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 hover:border-gray-300 rounded-lg bg-white transition disabled:opacity-40 disabled:cursor-not-allowed"
      title="Regenerate index.html, sitemap.xml and guides-registry.js only"
    >
      <RefreshCw size={13} />
      {generating ? 'Generating...' : 'Regenerate Index'}
    </button>
  );
};
